import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Button, Container, Typography, Box, FormControlLabel, Checkbox } from '@mui/material';
import axios from "axios";
import { useLocation } from 'react-router-dom';
import FilterComponent from './Filter';
import SearchComponent from './Search';
import FilterReleases from './FilterReleases';
import Music from './Music';
import releasesData from '../data/releases.json';
import { useTheme } from '@mui/material/styles';
import NorthIcon from '@mui/icons-material/North';
import PurchaseLockAnimation from './PurchaseLockAnimation';
import { useAudioPlayer } from './AudioPlayer';
import { PURCHASE_STATES, PURCHASE_STATE_LABELS } from '../constants/purchaseStates';
import { getStoredAudioUrl, getStoredSessionId, storeVerifiedEntitlement, clearStoredEntitlement } from '../utils/purchaseEntitlement';

const releaseTypeOptions = ["Single", "Album", "Mixtape", "Video"];
const PAGE_SIZE = 8;
const MAX_ITEMS = 500; // Set a limit to prevent performance issues

const MainPage = () => {
    const { openPlayer, hidePlayer } = useAudioPlayer();
    const [releases, setReleases] = useState([]);
    const [filteredReleases, setFilteredReleases] = useState([]);
    const [selectedRelease, setSelectedRelease] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const theme = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(true);
    const initialAudioUrl = getStoredAudioUrl();
    const [purchaseState, setPurchaseState] = useState(
        initialAudioUrl ? PURCHASE_STATES.UNLOCKED : PURCHASE_STATES.LOCKED
    );
    const [purchaseError, setPurchaseError] = useState('');
    const [audioUrlMP3, setAudioUrlMP3] = useState(initialAudioUrl);
    const location = useLocation();
    const officialiframeStyle = window.innerWidth <= 768 ? 
        { maxWidth: '100%', height: '152px' } : 
        { maxWidth: '100%', height: '352px' };
    const reverbnationiframeStyle = window.innerWidth <= 768 ? 
        { maxWidth: '100%', height: '265px' } : 
        { maxWidth: '100%', height: '265px' };
    const soundCloudiframeStyle = window.innerWidth <= 768 ? 
        { maxWidth: '100%', height: '152px' } : 
        { maxWidth: '100%', height: '275px' };
    const youTubeiframeStyle = window.innerWidth <= 768 ? 
        { border: 'none', maxWidth: '100%', height: '195px' } : 
        { border: 'none', maxWidth: '100%', height: '325px' };

    const verifyEntitlement = useCallback(async (sessionId, { showVerifyingState = false } = {}) => {
        if (!sessionId) {
            setPurchaseState(PURCHASE_STATES.LOCKED);
            return false;
        }

        try {
            if (showVerifyingState) {
                setPurchaseState(PURCHASE_STATES.VERIFYING);
            }
            setPurchaseError('');

            const response = await axios.post('/api/verify-payment', { sessionId });
            if (response.data?.verified && response.data?.urlMP3) {
                setAudioUrlMP3(response.data.urlMP3);
                storeVerifiedEntitlement({ audioUrl: response.data.urlMP3, sessionId });
                setPurchaseState(PURCHASE_STATES.UNLOCKED);
                return true;
            }

            clearStoredEntitlement();
            setAudioUrlMP3('');
            setPurchaseState(PURCHASE_STATES.LOCKED);
            return false;
        } catch (error) {
            setPurchaseError('We could not verify your purchase yet. Please try again.');
            setPurchaseState(showVerifyingState ? PURCHASE_STATES.ERROR : PURCHASE_STATES.LOCKED);
            return false;
        }
    }, []);

    const startCheckout = async () => {
        try {
            setPurchaseError('');
            setPurchaseState(PURCHASE_STATES.CHECKOUT_STARTING);
            const resp = await axios.post("/api/create-checkout-session", { marketingOptIn });
            setPurchaseState(PURCHASE_STATES.REDIRECTING);
            window.location.href = resp.data.url;
        } catch (e) {
            setPurchaseError('Checkout failed to start. Please try again.');
            setPurchaseState(PURCHASE_STATES.ERROR);
        }
    };

    const retryPurchaseFlow = async () => {
        const sessionId = getStoredSessionId();
        if (sessionId) {
            await verifyEntitlement(sessionId, { showVerifyingState: true });
            return;
        }
        await startCheckout();
    };

    const currentReleases = filteredReleases.slice(0, currentPage * PAGE_SIZE);

    const timeoutIdRef = useRef();

    const loadMoreItems = useCallback(() => {
        // Check if all items are already loaded
        if (currentReleases.length >= filteredReleases.length || currentPage * PAGE_SIZE >= MAX_ITEMS) {
            setLoading(false);
            return;
        }
        setLoading(true);
        
        // Introduce a delay before setting loading to false and incrementing the page
        timeoutIdRef.current = setTimeout(() => {
            setCurrentPage(prevPage => prevPage + 1);
            setLoading(false);
        }, 300); // 300 milliseconds = 0.3 second
    }, [currentPage, currentReleases.length, filteredReleases.length]);

    useEffect(() => {
        setReleases(releasesData);
        setFilteredReleases(releasesData);
        if (releasesData.length > 0) {
            setSelectedRelease(releasesData[0]);
        }
    }, []);

    useEffect(() => {
        const filtered = FilterReleases(releases, { searchTerm, filterType /*, filterDate, filterFormat*/ });
        setFilteredReleases(filtered);
        if (filtered.length > 0) {
            setSelectedRelease(filtered[0]);
        } else {
            setSelectedRelease(null);
        }
    }, [searchTerm, filterType, releases /*, filterDate, filterFormat*/]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight) return;
            loadMoreItems();
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutIdRef.current); // Clear the timeout
        };
    }, [loadMoreItems]); // Include loadMoreItems in the dependency array

    useEffect(() => {
        if (!selectedRelease || selectedRelease.platform !== 'Unreleased') return;
        if (audioUrlMP3) {
            setPurchaseState(PURCHASE_STATES.UNLOCKED);
            openPlayer({ title: selectedRelease.title, url: audioUrlMP3, sessionId: getStoredSessionId() }, { restore: true });
            return;
        }

        const sessionId = getStoredSessionId();
        if (!sessionId) {
            setPurchaseState(PURCHASE_STATES.LOCKED);
            return;
        }

        const isCheckoutReturn = location.search.includes('session_id=');
        verifyEntitlement(sessionId, { showVerifyingState: isCheckoutReturn });
    }, [location.search, selectedRelease, audioUrlMP3, verifyEntitlement, openPlayer]);

    const renderURLEmbed = () => {
        if (selectedRelease && selectedRelease.releaseType !== "Video") {
            if (selectedRelease.platform === "Official") {
                return (
                    <iframe
                        title={`${selectedRelease.title} Widget`}
                        width="100%"
                        scrolling="no"
                        frameborder="0"
                        src={selectedRelease.embedUrl}
                        style={officialiframeStyle}>
                    </iframe>
                );
            } else if (selectedRelease.platform === "Reverbnation") {
                return (
                    <iframe
                        title={`${selectedRelease.title} Widget`}
                        width="100%"
                        scrolling="no"
                        frameborder="0"
                        src={selectedRelease.embedUrl}
                        style={reverbnationiframeStyle}>
                    </iframe>
                );
            } else if (selectedRelease.platform === "Unreleased") {
                if (audioUrlMP3) {
                    return (
                        <Box textAlign="center" sx={{ width: '100%', mt: 3 }}>
                            <Button variant="contained" onClick={() => openPlayer({
                                title: selectedRelease.title, url: audioUrlMP3, sessionId: getStoredSessionId(),
                            })}>Open player</Button>
                            <Typography variant="h6" marginTop={3}>
                                Thank you for your support!
                            </Typography>
                        </Box>
                    );
                } else {
                    const disableAction = [
                        PURCHASE_STATES.CHECKOUT_STARTING,
                        PURCHASE_STATES.REDIRECTING,
                        PURCHASE_STATES.VERIFYING,
                    ].includes(purchaseState);

                    return (
                        <Box textAlign="center" sx={{ width: '100%' }}>
                            <Box sx={{ mb: 1 }}>
                                <PurchaseLockAnimation state={purchaseState} />
                            </Box>
                            <Button
                                variant="contained"
                                onClick={purchaseState === PURCHASE_STATES.ERROR ? retryPurchaseFlow : startCheckout}
                                disabled={disableAction}
                                sx={{ mt: 1 }}
                            >
                                {PURCHASE_STATE_LABELS[purchaseState] || PURCHASE_STATE_LABELS[PURCHASE_STATES.LOCKED]}
                            </Button>
                            <br/>
                            <FormControlLabel
                                control={
                                <Checkbox
                                    checked={marketingOptIn}
                                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                                    disabled={disableAction}
                                />
                                }
                                label="Email me when new music drops"
                            />
                            {purchaseError && (
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, mb: 0.5 }}>
                                    {purchaseError}
                                </Typography>
                            )}
                            {purchaseState === PURCHASE_STATES.ERROR && (
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, mb: 1 }}>
                                    If this keeps happening, contact support from the Contact page.
                                </Typography>
                            )}
                        </Box>
                    );
                }
            } else {
                return (
                    <iframe
                        title={`${selectedRelease.title} Widget`}
                        width="100%"
                        scrolling="no"
                        frameborder="0"
                        src={selectedRelease.embedUrl}
                        style={soundCloudiframeStyle}>
                    </iframe>
                );
            }
        } else {
            return (
                <iframe
                    title={`${selectedRelease.title} Video`}
                    src={selectedRelease.embedUrl}
                    width="575"
                    style={youTubeiframeStyle}
                    allowFullScreen>
                </iframe>
            );
        }
    };

    const renderLinkshareEmbed = () => {
        if (selectedRelease && selectedRelease.platform === "Unreleased") {
            return null;
        }

        if (selectedRelease && selectedRelease.linkshareURL && selectedRelease.releaseType !== "Video") {
            return (
                <iframe
                    title={`${selectedRelease.title} Linkshare`}
                    src={selectedRelease.linkshareURL}
                    width="100%"
                    height="52"
                    scrolling="no"
                    frameborder="0"
                    style={{ marginTop: '25px', maxWidth: '100%' }}>
                </iframe>
            );
        } else if (selectedRelease && selectedRelease.releaseType === "Video") {
            if (selectedRelease.linkshareURL) {
                return (
                    <iframe
                    title={`${selectedRelease.title} Linkshare`}
                    src={selectedRelease.linkshareURL}
                    width="100%"
                    height="52"
                    scrolling="no"
                    frameborder="0"
                    style={{ marginTop: '25px', maxWidth: '100%' }}>
                </iframe>
                );
            }
            return (
                <iframe
                title="YT Subscribe Button"
                src="https://www.youtube.com/subscribe_embed?channelid=UCLlTafwrWEXSrMb3VLfpfGw"
                width="115"
                height="25"
                style={{
                    marginTop: '20px',
                    border: 'none',
                }}>
                </iframe>
            );
        } else {
            return (
                <Typography
                    variant="body1"
                    align="center"
                    sx={{
                        marginTop: '20px',
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                    }}
                >
                    <NorthIcon/> This release is not on streaming platforms <NorthIcon/>
                </Typography>
            );
        }
    };

    const renderNoResultsMessage = () => {
        return (
            <Typography
                variant="h6"
                align="center"
                sx={{
                    marginTop: '20px',
                    fontSize: { xs: '1.25rem', sm: '1.75rem' },
                }}
            >
                No songs found.
            </Typography>
        );
    };

    return (
        <Container style={{
            marginTop: '25px',
            marginBottom: '25px',
            backgroundColor: theme.palette.background.default,
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center' }}>
            {selectedRelease && (
                <>
                    <Typography
                        variant="h6"
                        align="center"
                        color={theme.palette.text.primary}
                        sx={{
                            fontSize: { xs: '1.05rem', sm: '1.25rem' },
                        }}
                    >
                        {selectedRelease.title}
                    </Typography>
                    {renderURLEmbed()}
                    {renderLinkshareEmbed()}
                </>
            )}
            <Container style={{
                backgroundColor: theme.palette.background.default,
                marginTop: '20px',
                marginBottom: '1px',
                padding: '1px'
                }}>
                <Grid container spacing={4} alignItems="center" justifyContent="space-between">
                    <Grid item xs={2} sm={6} md={4} lg={3}>
                        <SearchComponent searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                    </Grid>

                    <Grid item container xs={8} sm={6} md={8} lg={9} justifyContent="flex-end" alignItems="center" spacing={1}>
                        <Grid item>
                            <FilterComponent filterType={filterType} setFilterType={setFilterType} filterOptions={releaseTypeOptions} />
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={() => setFilterType('')}
                                sx={{
                                    width: { xs: '50px', sm: '118px' },
                                    fontSize: {xs: '0.7rem', sm: '0.86rem'}
                                }}>
                                Clear Filter
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
            {/* Render other filters as needed */}

            <Typography
                variant="subtitle1"
                align="center"
                sx={{
                    marginTop: '15px',
                    marginBottom: '10px',
                    fontSize: { xs: '0.75rem', sm: '1rem' }
                }}
            >
                Total Releases Found: {filteredReleases.length}
            </Typography>

            <Music
                releases={currentReleases}
                onReleaseSelect={release => {
                    setSelectedRelease(release);
                    if (release.platform !== 'Unreleased') hidePlayer();
                    else if (audioUrlMP3) openPlayer({ title: release.title, url: audioUrlMP3, sessionId: getStoredSessionId() });
                }}
                unreleasedUnlocked={purchaseState === PURCHASE_STATES.UNLOCKED}
            />

            {filteredReleases.length === 0 && searchTerm !== '' && renderNoResultsMessage()}

            {loading && currentReleases.length < filteredReleases.length && (
                <Typography>Loading...</Typography>
            )}
        </Container>
    );
}

export default MainPage;
